/**
 * 캠페인 기간 → UI/데이터 로딩용 상태 (목록·하이라이트·브랜드 포스트 초기 로딩과 동일 규칙)
 */

import { toKstYmd } from 'src/utils/campaign/campaign-criteria';

/**
 * @param {Object} params
 * @param {*} params.rawStart - period.start
 * @param {*} params.rawEnd - period.end (undefined이면 진행중으로 간주하는 쪽에서 now 전달)
 * @param {Date} params.now
 * @param {Function} params.toDateObj - Firestore/문자열 → Date
 * @returns {'예정'|'진행중'|'종료'}
 */
export function getCampaignStatusLabelFromPeriod({
  rawStart,
  rawEnd,
  now,
  toDateObj,
}: {
  rawStart: unknown;
  rawEnd: unknown;
  now: Date;
  toDateObj: (val: any) => Date | null;
}): '예정' | '진행중' | '종료' {
  const sDate = rawStart != null ? toDateObj(rawStart) : null;
  if (sDate && sDate > now) return '예정';

  const effectiveEnd = rawEnd ?? now;
  const eDate = toDateObj(effectiveEnd) || now;

  // 날 경계는 KST 달력이다 — 서버(UTC)와 화면이 같은 날에 종료를 판정한다
  const today = toKstYmd(now);
  const endDay = toKstYmd(eDate);
  if (today && endDay && today > endDay) return '종료';

  return '진행중';
}

/**
 * 진행 중인 캠페인만 — 대시보드 수집 현황과 홈 예약 계산이 같은 판정을 쓴다(`getCampaignStatusLabelFromPeriod` === '진행중').
 * @param now 기준 시각 — 날짜 경계는 KST 달력이다
 */
export function filterInProgressCampaigns<T extends Record<string, any>>(campaigns: readonly T[] | null | undefined, now: Date): T[] {
  return (Array.isArray(campaigns) ? campaigns : []).filter((campaign) => (
    getCampaignStatusLabelFromPeriod({
      rawStart: campaign?.period?.start,
      rawEnd: campaign?.period?.end,
      now,
      toDateObj: toPeriodDate,
    }) === '진행중'
  ));
}

/** Firestore Timestamp / ISO / number → Date */
function toPeriodDate(value: any): Date | null {
  if (value == null) return null;
  try {
    const date: Date = typeof value?.toDate === 'function' ? value.toDate() : new Date(value);
    return Number.isNaN(date.getTime()) ? null : date;
  } catch {
    return null;
  }
}
